import styled from "@emotion/styled";
import {
  type GroupTranslation,
  type Widget,
  type WidgetType,
  type WorkspaceTemplateConfig,
  type WorkspaceTemplateWidget,
  type WorkspaceTemplateWidgetEntry,
} from "@repo/api";
import { List, Loading, Option, Select, Text } from "@repo/skapa";
import { T, t } from "@repo/translation";
import {
  ConfirmModal,
  ElementsContainer,
  SectionContainer,
  translateLabel,
  useSettings,
  useToast,
} from "@repo/ui";
import { createContext, useContext, useMemo, useState } from "react";

import { useApplyTemplate } from "./useApplyTemplate";
import { useWorkspaceTemplates } from "./useWorkspaceTemplates";

const OuterContainer = styled(SectionContainer)`
  flex: 1;
  min-height: 100%;
`;

const widgetTypeFallbackLabel = (widgetType: WidgetType) =>
  widgetType
    .toLowerCase()
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const toTemplateWidget = (
  entry: WorkspaceTemplateWidgetEntry
): WorkspaceTemplateWidget =>
  typeof entry === "string" ? { widgetType: entry } : entry;

const getWidgetLabel = (widgetType: WidgetType, widgets?: Widget[]) => {
  const widget = widgets?.find((w) => w.widgetType === widgetType);
  return (
    (widget && translateLabel(widget.name)) ||
    widgetTypeFallbackLabel(widgetType)
  );
};

const pickTranslation = (
  translations: GroupTranslation[] | undefined,
  language: string,
  fallback: string
) =>
  translations?.find((tr) => tr.language_key === language)?.translation ??
  fallback;

interface TemplateSelectionContextValue {
  templates: WorkspaceTemplateConfig[] | undefined;
  isLoading: boolean;
  selectedTemplateName: string | undefined;
  setSelectedTemplateName: (name: string) => void;
  selectedTemplate: WorkspaceTemplateConfig | undefined;
  isWidgetAvailable: (widgetType: WidgetType) => boolean;
  getLabelForWidget: (widgetType: WidgetType) => string;
  getTemplateName: (template: WorkspaceTemplateConfig) => string;
  getTemplateDescription: (template: WorkspaceTemplateConfig) => string;
  isApplying: boolean;
  showConfirm: boolean;
  setShowConfirm: (v: boolean) => void;
  handleApply: () => Promise<void>;
  onResetWorkspace?: () => void;
}

const TemplateSelectionContext = createContext<
  TemplateSelectionContextValue | undefined
>(undefined);

const useTemplateSelectionContext = () => {
  const ctx = useContext(TemplateSelectionContext);
  if (!ctx) {
    throw new Error(
      "TemplateSelection components must be used inside <TemplateSelectionProvider>"
    );
  }
  return ctx;
};

interface TemplateSelectionProviderProps {
  availableWidgets?: Widget[];
  onResetWorkspace?: () => void;
  children: React.ReactNode;
}

export const TemplateSelectionProvider = ({
  availableWidgets,
  onResetWorkspace,
  children,
}: TemplateSelectionProviderProps) => {
  const { updateToast } = useToast();
  const { language } = useSettings();
  const { applyTemplate, isApplying, lastAppliedTemplate } = useApplyTemplate(
    updateToast,
    availableWidgets
  );
  const { workspaceTemplates, isLoading: isTemplatesLoading } =
    useWorkspaceTemplates();
  const [selectedTemplateName, setSelectedTemplateName] = useState<
    string | undefined
  >(lastAppliedTemplate);
  const [showConfirm, setShowConfirm] = useState(false);

  const availableWidgetTypes = availableWidgets?.map((w) => w.widgetType);
  const templates = workspaceTemplates?.templates;

  const selectedTemplate = templates?.find(
    (tpl) => tpl.name === selectedTemplateName
  );

  const value = useMemo<TemplateSelectionContextValue>(() => {
    const isWidgetAvailable = (widgetType: WidgetType) =>
      !availableWidgetTypes || availableWidgetTypes.includes(widgetType);

    const getLabelForWidget = (widgetType: WidgetType) =>
      getWidgetLabel(widgetType, availableWidgets);

    const getTemplateName = (template: WorkspaceTemplateConfig) =>
      pickTranslation(template.name_translation, language, template.name);

    const getTemplateDescription = (template: WorkspaceTemplateConfig) =>
      pickTranslation(
        template.description_translation,
        language,
        template.description
      );

    const handleApply = async () => {
      if (!selectedTemplate) {
        return;
      }
      const filteredTemplate = availableWidgetTypes
        ? {
            ...selectedTemplate,
            widgetTypes: selectedTemplate.widgetTypes.filter((entry) =>
              availableWidgetTypes.includes(toTemplateWidget(entry).widgetType)
            ),
          }
        : selectedTemplate;
      await applyTemplate(filteredTemplate);
    };

    return {
      templates,
      isLoading: isTemplatesLoading,
      selectedTemplateName,
      setSelectedTemplateName,
      selectedTemplate,
      isWidgetAvailable,
      getLabelForWidget,
      getTemplateName,
      getTemplateDescription,
      isApplying,
      showConfirm,
      setShowConfirm,
      handleApply,
      onResetWorkspace,
    };
  }, [
    templates,
    isTemplatesLoading,
    selectedTemplateName,
    selectedTemplate,
    availableWidgets,
    availableWidgetTypes,
    applyTemplate,
    isApplying,
    showConfirm,
    onResetWorkspace,
    language,
  ]);

  return (
    <TemplateSelectionContext.Provider value={value}>
      {children}
      <ConfirmModal
        visible={showConfirm}
        onConfirm={value.handleApply}
        onCancel={() => setShowConfirm(false)}
        onClose={() => setShowConfirm(false)}
        title={t("prompt.confirm_apply_template_title")}
        message={t("prompt.confirm_apply_template")}
      />
    </TemplateSelectionContext.Provider>
  );
};

export const TemplateSelection = () => {
  const {
    templates,
    isLoading,
    selectedTemplateName,
    setSelectedTemplateName,
    selectedTemplate,
    isWidgetAvailable,
    getLabelForWidget,
    getTemplateName,
    getTemplateDescription,
  } = useTemplateSelectionContext();

  if (isLoading) {
    return <Loading text="" />;
  }

  if (!templates?.length) {
    return null;
  }

  return (
    <OuterContainer>
      <SectionContainer>
        <Select
          id="template-select"
          label={t("label.select_template")}
          value={selectedTemplateName}
          onChange={(e) => setSelectedTemplateName(e.target.value)}
        >
          {templates.map((template) => (
            <Option
              key={template.name}
              value={template.name}
              name={getTemplateName(template)}
            />
          ))}
        </Select>
      </SectionContainer>

      {selectedTemplate && (
        <>
          <ElementsContainer>
            <Text textStyle="Heading.S">
              <T translationKey="label.description" />
            </Text>
            <Text>{getTemplateDescription(selectedTemplate)}</Text>
          </ElementsContainer>

          <ElementsContainer>
            <Text textStyle="Heading.S">
              <T translationKey="label.widgets_in_template" />
            </Text>
            <List size="medium">
              {selectedTemplate.widgetTypes.map((entry) => {
                const { widgetType } = toTemplateWidget(entry);
                return (
                  <li key={widgetType}>
                    {getLabelForWidget(widgetType)}
                    {!isWidgetAvailable(widgetType) &&
                      ` (${t("label.widget_not_available")})`}
                  </li>
                );
              })}
            </List>
          </ElementsContainer>
        </>
      )}
    </OuterContainer>
  );
};

export const useTemplateSelectionActions = () => {
  const {
    selectedTemplate,
    isApplying,
    setShowConfirm,
    onResetWorkspace,
    templates,
  } = useTemplateSelectionContext();

  return {
    hasTemplates: Boolean(templates?.length),
    isApplying,
    applyDisabled: !selectedTemplate || isApplying,
    onApply: () => setShowConfirm(true),
    onReset: onResetWorkspace,
  };
};
